import React, { useState } from 'react';
import {
  Box,
  TextField,
  Grid,
  Typography,
  Divider,
  Collapse,
  IconButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import type { Person, MilitaryService } from '../../../../types';

interface ProfessionalFormProps {
  data: Partial<Person>;
  errors: Record<string, string>;
  onChange: (field: string, value: any) => void;
  disabled?: boolean;
}

/**
 * ProfessionalForm - Edit professional information
 *
 * Fields:
 * - Occupation
 * - Education
 * - Military Service (expandable section)
 */
export const ProfessionalForm: React.FC<ProfessionalFormProps> = ({
  data,
  errors,
  onChange,
  disabled = false
}) => {
  const [militaryExpanded, setMilitaryExpanded] = useState(!!data.militaryService);

  const handleMilitaryChange = (field: keyof MilitaryService, value: any) => {
    const currentMilitary = data.militaryService || {
      branch: '',
      rank: '',
      serviceNumber: '',
      startDate: '',
      endDate: '',
      unit: '',
      conflicts: [],
      decorations: [],
      stationed: []
    };
    onChange('militaryService', {
      ...currentMilitary,
      [field]: value
    });
  };

  const militaryService = data.militaryService;

  return (
    <Box sx={{ pt: 2 }}>
      <Grid container spacing={3}>
        {/* Occupation */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Occupation"
            value={data.occupation || ''}
            onChange={(e) => onChange('occupation', e.target.value)}
            error={!!errors.occupation}
            helperText={errors.occupation}
            disabled={disabled}
          />
        </Grid>

        {/* Education */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Education"
            value={data.education || ''}
            onChange={(e) => onChange('education', e.target.value)}
            error={!!errors.education}
            helperText={errors.education}
            disabled={disabled}
          />
        </Grid>

        {/* Military Service Section */}
        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Military Service (Optional)
            </Typography>
            <IconButton
              size="small"
              onClick={() => setMilitaryExpanded(!militaryExpanded)}
              disabled={disabled}
            >
              {militaryExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Collapse in={militaryExpanded}>
            <Grid container spacing={2}>
              {/* Branch */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Branch"
                  value={militaryService?.branch || ''}
                  onChange={(e) => handleMilitaryChange('branch', e.target.value)}
                  disabled={disabled}
                />
              </Grid>

              {/* Rank */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Rank"
                  value={militaryService?.rank || ''}
                  onChange={(e) => handleMilitaryChange('rank', e.target.value)}
                  disabled={disabled}
                />
              </Grid>

              {/* Service Number */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Service Number"
                  value={militaryService?.serviceNumber || ''}
                  onChange={(e) => handleMilitaryChange('serviceNumber', e.target.value)}
                  disabled={disabled}
                />
              </Grid>

              {/* Unit */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Unit"
                  value={militaryService?.unit || ''}
                  onChange={(e) => handleMilitaryChange('unit', e.target.value)}
                  disabled={disabled}
                />
              </Grid>

              {/* Start Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  value={militaryService?.startDate || ''}
                  onChange={(e) => handleMilitaryChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={disabled}
                />
              </Grid>

              {/* End Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  value={militaryService?.endDate || ''}
                  onChange={(e) => handleMilitaryChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={disabled}
                />
              </Grid>

              {/* Conflicts */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Conflicts"
                  value={
                    militaryService && Array.isArray(militaryService.conflicts)
                      ? militaryService.conflicts.join(', ')
                      : ''
                  }
                  onChange={(e) => handleMilitaryChange('conflicts', e.target.value.split(',').map(c => c.trim()).filter(Boolean))}
                  helperText="Separate multiple conflicts with commas"
                  disabled={disabled}
                />
              </Grid>

              {/* Decorations */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Decorations/Medals"
                  value={
                    militaryService && Array.isArray(militaryService.decorations)
                      ? militaryService.decorations.join(', ')
                      : ''
                  }
                  onChange={(e) => handleMilitaryChange('decorations', e.target.value.split(',').map(d => d.trim()).filter(Boolean))}
                  helperText="Separate multiple decorations with commas"
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Grid>
      </Grid>
    </Box>
  );
};
